import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { generateApplicationId } from '../utils/idGenerator';
import { FiCheck, FiUser, FiMail, FiPhone, FiBriefcase, FiSend } from 'react-icons/fi';
import { useParams } from 'react-router-dom';

export default function PublicApply() {
  const { orgSlug } = useParams();
  const [submitted, setSubmitted] = useState(false);
  const [appId, setAppId] = useState('');
  const [form, setForm] = useState({
    name: '', email: '', phone: '', college: '', role: '', department: '', coverNote: ''
  });

  const ROLES = ['Frontend Intern', 'Backend Intern', 'Full Stack Intern', 'UI/UX Intern', 'Data Science Intern', 'ML Intern', 'DevOps Intern', 'Marketing Intern', 'HR Intern'];
  const DEPTS = ['Engineering', 'Design', 'Marketing', 'Finance', 'HR', 'Data Science', 'Operations'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Name and Email are required.');
    const id = generateApplicationId();
    setAppId(id);
    setSubmitted(true);
    // In a real app this would POST to server; here we just show confirmation
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50 flex items-center justify-center p-8">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-lg w-full bg-white rounded-[40px] p-12 text-center shadow-2xl shadow-sky-500/10 border border-slate-100">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <FiCheck className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-navy-900 mb-3">Application Submitted!</h1>
          <p className="text-slate-500 mb-6">Thank you, <strong>{form.name}</strong>. Your internship application has been received.</p>
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 mb-8">
            <div className="section-title mb-2">Your Application ID</div>
            <div className="font-mono font-semibold text-brand-600 text-xl">{appId}</div>
          </div>
          <p className="text-sm text-slate-700 font-medium">We will review your application and get back to you within 5 business days at <strong>{form.email}</strong>.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50 flex items-center justify-center p-8">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 border border-sky-100 rounded-full text-sky-700 text-[11px] font-black uppercase tracking-widest mb-4">
            <FiBriefcase className="w-3.5 h-3.5" /> Internship Application
          </div>
          <h1 className="text-4xl font-semibold text-navy-900 tracking-tight mb-2">Apply for an Internship</h1>
          <p className="text-slate-800 font-bold">at <span className="text-sky-600 font-black capitalize">{(orgSlug || 'organization').replace(/-/g, ' ')}</span></p>
        </div>

        <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit}
          className="bg-white rounded-[32px] p-10 shadow-2xl shadow-sky-500/5 border border-slate-100 space-y-6">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { key: 'name', label: 'Full Name', type: 'text', icon: FiUser, req: true, placeholder: 'John Doe' },
              { key: 'email', label: 'Email Address', type: 'email', icon: FiMail, req: true, placeholder: 'john@example.com' },
              { key: 'phone', label: 'Phone Number', type: 'tel', icon: FiPhone, placeholder: '+91 98765 43210' },
              { key: 'college', label: 'College / Institution', type: 'text', placeholder: 'ABC University' },
            ].map(f => (
              <div key={f.key}>
                <label className="block section-title mb-2">{f.label}{f.req ? ' *' : ''}</label>
                <div className="relative">
                  {f.icon && <f.icon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 w-4 h-4" />}
                  <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className={`w-full border border-slate-200 rounded-2xl py-3.5 pr-4 ${f.icon ? 'pl-12' : 'pl-4'} text-sm focus:outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/5 transition-all`} />
                </div>
              </div>
            ))}

            <div>
              <label className="block section-title mb-2">Applied Role</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="w-full border border-slate-200 rounded-2xl px-4 py-3.5 text-sm focus:outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/5 transition-all bg-white appearance-none">
                <option value="">Select a Role...</option>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div>
              <label className="block section-title mb-2">Department</label>
              <select value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))} className="w-full border border-slate-200 rounded-2xl px-4 py-3.5 text-sm focus:outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/5 transition-all bg-white appearance-none">
                <option value="">Select Department...</option>
                {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block section-title mb-2">Cover Note</label>
            <textarea value={form.coverNote} onChange={e => setForm(p => ({ ...p, coverNote: e.target.value }))} rows={5}
              placeholder="Tell us about yourself, your skills, and why you're interested in this internship..."
              className="w-full border border-slate-200 rounded-2xl px-4 py-3.5 text-sm focus:outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/5 transition-all resize-none" />
          </div>

          <button type="submit" className="w-full flex items-center justify-center gap-3 py-4 bg-sky-600 hover:bg-sky-500 text-white rounded-2xl font-black text-sm uppercase tracking-widest transition-all shadow-xl shadow-sky-500/25 hover:shadow-2xl hover:shadow-sky-500/30 hover:-translate-y-0.5 active:translate-y-0">
            <FiSend className="w-4 h-4" /> Submit Application
          </button>

          <p className="text-center text-[11px] text-slate-300 font-bold">Your data is protected and will only be used for recruitment purposes.</p>
        </motion.form>
      </div>
    </div>
  );
}
